/* @flow */

import * as React from 'react';
import classNames from 'classnames';
import { noop, omit } from 'lodash/fp';
import { getLuminance } from 'polished';
import RewriteLink from 'app/components/routing/RewriteLink';
import useSheet from '../../jss';
import theme from '../../theme';
import buttonSizeTypes from '../../constants/buttonSizeTypes';
import { makeSimpleButtonStyle } from '../../utils/buttonUtils';

type Kind =
  | 'bare'
  | 'danger'
  | 'success'
  | 'primary'
  | 'primaryNoGradient'
  | 'playup'
  | 'secondary'
  | 'warning'
  | 'reset'
  | 'cancel'
  | 'cancel-playup'
  | 'delete'
  | 'deleteNoGradient'
  | 'info'
  | 'terms';

type Props = {
  autoBorderRadius?: boolean,
  block?: boolean,
  className?: string,
  children?: any,
  disabled?: boolean,
  fake?: boolean,
  href?: string,
  kind?: Kind,
  link?: string | Object,
  onClick?: () => void,
  rel?: string,
  sheet: Object,
  size?: buttonSizeTypes,
  style?: Object,
  submit?: boolean,
  target?: '_self' | '_blank' | '_parent' | '_top'
};

const PLAYUP_GREEN = '#01ca6e';

const defaultProps = {
  autoBorderRadius: true,
  block: false,
  className: undefined,
  children: undefined,
  disabled: false,
  fake: false,
  href: undefined,
  kind: 'primary',
  link: undefined,
  onClick: noop,
  rel: undefined,
  size: 'default',
  style: undefined,
  submit: false,
  target: '_self'
};

const buttonConfig = {
  tiny: {
    fontSize: theme.fontSize.tiny,
    borderRadius: theme.borderRadius.tiny,
    lineHeight: `${buttonSizeTypes.tiny}px`,
    minHeight: `${buttonSizeTypes.tiny}px`,
    padding: '0 8px'
  },
  small: {
    fontSize: theme.fontSize.small,
    borderRadius: theme.borderRadius.small,
    lineHeight: `${buttonSizeTypes.small}px`,
    minHeight: `${buttonSizeTypes.small}px`,
    padding: `0 ${theme.spacing.small}px`
  },
  default: {
    fontSize: theme.fontSize.default,
    borderRadius: theme.borderRadius.default - 2,
    lineHeight: `${buttonSizeTypes.default}px`,
    minHeight: `${buttonSizeTypes.default}px`,
    padding: `0 ${theme.spacing.default / 2}px`
  },
  medium: {
    fontSize: theme.fontSize.medium,
    borderRadius: theme.borderRadius.medium,
    lineHeight: `${buttonSizeTypes.medium}px`,
    minHeight: `${buttonSizeTypes.medium}px`,
    padding: `0 ${theme.spacing.medium / 2}px`
  },
  claim: {
    fontSize: theme.fontSize.large,
    borderRadius: theme.borderRadius.medium,
    lineHeight: `${buttonSizeTypes.medium}px`,
    minHeight: `${buttonSizeTypes.medium}px`,
    padding: `0 ${theme.spacing.medium / 2}px`
  },
  large: {
    fontSize: theme.fontSize.large,
    borderRadius: theme.borderRadius.large,
    lineHeight: `${buttonSizeTypes.large}px`,
    minHeight: `${buttonSizeTypes.large}px`,
    padding: `0 ${theme.spacing.large / 2}px`
  }
};

function Button(props: Props) {
  const {
    autoBorderRadius,
    block,
    className,
    children,
    disabled,
    fake,
    href,
    kind,
    link,
    onClick,
    rel,
    size = defaultProps.size,
    sheet: { classes },
    style,
    submit,
    target
  } = props;

  const buttonStyle = {
    ...omit(autoBorderRadius ? '' : 'borderRadius')(buttonConfig[size]),
    ...style
  };

  const buttonClass = classNames(
    classes.button,
    classes[kind],
    {
      [classes.block]: block,
      disabled
    },
    className
  );

  if (fake) {
    return (
      <span className={buttonClass} style={buttonStyle}>
        {children}
      </span>
    );
  }

  if (href) {
    return (
      <a
        href={href}
        className={buttonClass}
        style={buttonStyle}
        onClick={onClick}
        disabled={disabled}
        rel={rel}
        target={target}
      >
        {children}
      </a>
    );
  }

  if (link) {
    return (
      <RewriteLink
        to={link}
        className={buttonClass}
        style={buttonStyle}
        onClick={onClick}
        disabled={disabled}
      >
        {children}
      </RewriteLink>
    );
  }

  return (
    <button
      type={submit ? 'submit' : 'button'}
      className={buttonClass}
      style={buttonStyle}
      onClick={(event, ...args) => {
        event.preventDefault();
        onClick(...args);
      }}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

Button.defaultProps = defaultProps;

function applyStyles({ fontWeight, color, textTransform = 'uppercase' }) {
  const baseStyle = {
    button: {
      backgroundColor: color.white,
      border: 'none',
      boxShadow: 'none',
      fontWeight: fontWeight.demiBold,
      height: 'auto',
      display: 'inline-block',
      paddingBottom: 0,
      paddingTop: 0,
      textDecoration: 'none',
      textTransform,
      '&:hover, &:active, &:focus': {
        textDecoration: 'none'
      },
      [theme.mediaQuery.tabletLandscapeAndBelow]: {
        borderRadius: 0
      }
    },
    block: {
      alignSelf: 'center',
      display: 'block',
      textAlign: 'center',
      width: '100%'
    }
  };

  // all buttons have a possible 'disabled' state
  const disabledConfig = { disabledColor: color.basis6, disabledBackground: color.basis4 };

  // This is our set of buttons we want to support
  // To add another, craft another object.assign and append to list.
  // This will generate a button with a normal, hover, active, disabled and disabled hover styles.
  const buttons = [
    {
      name: 'primary',
      background: color.primary,
      color: getLuminance(color.primary) > 0.5 ? 'black' : 'white'
    },
    {
      name: 'secondary',
      background: color.basis2,
      color: color.white
    },
    {
      name: 'reset',
      background: color.basis4,
      color: color.basis2
    },
    {
      name: 'delete',
      background: color.basis4,
      color: color.white
    },
    {
      name: 'success',
      background: color.success,
      color: color.white
    },
    {
      name: 'playup',
      background: PLAYUP_GREEN,
      color: color.white
    },
    {
      name: 'warning',
      background: color.warning,
      color: color.white
    },
    {
      name: 'danger',
      background: color.danger,
      color: color.white
    },
    {
      name: 'info',
      background: color.info,
      color: color.white
    },
    {
      name: 'cancel',
      color: color.basis4
    },
    {
      name: 'cancel-playup',
      color: color.basis4,
      textTransform: 'none',
      textDecoration: 'underline',
      fontWeight: 'normal'
    },
    {
      name: 'terms',
      color: color.basis4,
      textTransform: 'none',
      fontWeight: 'normal'
    },
    {
      name: 'bare',
      background: 'transparent',
      color: color.basis4,
      hoverColor: color.basis3,
      activeColor: color.basis3,
      hoverBackground: 'transparent'
    }
  ].map(config =>
    makeSimpleButtonStyle({
      ...config,
      ...disabledConfig
    })
  );

  return Object.assign(baseStyle, ...buttons);
}

export default useSheet(applyStyles)(Button);
